import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { gql, Apollo } from 'apollo-angular';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionCartService {
  private cartSubject = new BehaviorSubject<any>({});
  public cart$ = this.cartSubject.asObservable();

  constructor(private apollo: Apollo) {
    this.loadCartFromStorage();
  }

  /**
   * Load cart from localStorage on initialization
   */
  private loadCartFromStorage() {
    const savedCart = localStorage.getItem('CART_SUBSCRIPTION');
    if (savedCart) {
      try {
        this.cartSubject.next(JSON.parse(savedCart));
      } catch (e) {
        console.error('Error loading cart from storage:', e);
        this.cartSubject.next({});
      }
    }
  }

  /**
   * Get current cart
   */
  getCart(): any {
    return this.cartSubject.value;
  }

  /**
   * Add item to cart
   */
  addToCart(product: any, type: 'subscription' | 'addon' | 'voucher', quantity: number = 1): void {
    const currentCart = this.cartSubject.value;
    
    if (!currentCart[type]) {
      currentCart[type] = [];
    }

    // Check if product already exists
    const existingIndex = currentCart[type].findIndex(
      (item: any) => item.fhProductOfferingId === product.fhProductOfferingId
    );

    if (existingIndex > -1) {
      // Update quantity if exists
      currentCart[type][existingIndex].quantity = 
        (currentCart[type][existingIndex].quantity || 1) + quantity;
    } else {
      // Add new item
      currentCart[type].push({
        ...product,
        quantity: quantity,
        type: type,
        addedAt: new Date().toISOString()
      });
    }

    this.saveCart(currentCart);
  }

  /**
   * Remove item from cart
   */
  removeFromCart(productOfferingId: string, type: string): void {
    const currentCart = this.cartSubject.value;
    
    if (currentCart[type]) {
      currentCart[type] = currentCart[type].filter(
        (item: any) => item.fhProductOfferingId !== productOfferingId
      );

      if (currentCart[type].length === 0) {
        delete currentCart[type];
      }

      this.saveCart(currentCart);
    }
  }

  /**
   * Update item quantity
   */
  updateQuantity(productOfferingId: string, type: string, quantity: number): void {
    const currentCart = this.cartSubject.value;
    
    if (currentCart[type]) {
      const item = currentCart[type].find(
        (item: any) => item.fhProductOfferingId === productOfferingId
      );

      if (item) {
        if (quantity <= 0) {
          this.removeFromCart(productOfferingId, type);
        } else {
          item.quantity = quantity;
          this.saveCart(currentCart);
        }
      }
    }
  }

  /**
   * Clear entire cart
   */
  clearCart(): void {
    this.cartSubject.next({});
    localStorage.removeItem('CART_SUBSCRIPTION');
  }

  /**
   * Get cart total count
   */
  getCartCount(): number {
    let count = 0;
    const cart = this.cartSubject.value;
    
    Object.keys(cart).forEach(type => {
      if (Array.isArray(cart[type])) {
        count += cart[type].length;
      }
    });

    return count;
  }

  /**
   * Get cart total price
   */
  getCartTotal(): number {
    let total = 0;
    const cart = this.cartSubject.value;

    Object.keys(cart).forEach(type => {
      if (Array.isArray(cart[type])) {
        cart[type].forEach((item: any) => {
          let price = 0;

          switch (type) {
            case 'subscription':
              price = item.subscriptionPrice || 0;
              break;
            case 'addon':
              price = item.addOnPrice || 0;
              break;
            case 'voucher':
              price = item.voucherPrice || 0;
              break;
          }

          // Apply discount if available
          let discount = 0;
          switch (type) {
            case 'subscription':
              discount = item.subscriptionDiscount || 0;
              break;
            case 'addon':
              discount = item.addOnDiscount || 0;
              break;
            case 'voucher':
              discount = item.voucherDiscount || 0;
              break;
          }

          const discountAmount = discount;
          const finalPrice = (price - discountAmount) * (item.quantity || 1);
          total += finalPrice;
        });
      }
    });

    return total;
  }

  /**
   * Get items by type
   */
  getItemsByType(type: string): any[] {
    const cart = this.cartSubject.value;
    return cart[type] || [];
  }

  /**
   * Save cart to localStorage and emit changes
   */
  private saveCart(cart: any): void {
    this.cartSubject.next(cart);
    localStorage.setItem('CART_SUBSCRIPTION', JSON.stringify(cart));
  }

  /**
   * Validate cart before checkout (can be extended with more validations)
   */
  validateCart(): { valid: boolean; errors: string[] } {
    const errors: string[] = [];
    const cart = this.cartSubject.value;

    if (Object.keys(cart).length === 0) {
      errors.push('Cart is empty');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}
